import { useEffect, useState } from "react";
import { Button, InputAdornment, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import MainContainer from "../../layout/MainContainer";
import { errorToast, successToast } from "../../helpers";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import Loader from "../../constants/Loader";

import { useLazyGetCmsQuery, useUpdateCmsMutation } from "../../service/cms";

const Settings = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const [getCms] = useLazyGetCmsQuery();
  const [updateCms] = useUpdateCmsMutation();

  const getCmsData = async () => {
    setLoading(true);
    try {
      const response = await getCms().unwrap();
      setLoading(false);

      if (response?.status === 200) {
        formik.setFieldValue("vat", response?.data?.vat || "");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vat: "",
    },

    validationSchema: Yup.object({
      vat: Yup.string().required("This field is required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        vat: values.vat,
      };

      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await updateCms({
          body: encryptedBody,
        }).unwrap();
        if (response?.status === 200) {
          successToast("Updated successfully!");
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
      }
    },
  });

  useEffect(() => {
    getCmsData();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1> Settings</h1>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>VAT</label>
              <TextField
                variant="outlined"
                placeholder="VAT"
                fullWidth
                hiddenLabel
                className="text_field"
                name="vat"
                id="vat"
                value={formik.values.vat}
                onChange={(val) => {
                  const inputValue = val.target.value;

                  // Allow empty value
                  if (inputValue === "") {
                    formik.handleChange(val);
                    return;
                  }

                  // Allow only valid decimal numbers (max 2 decimal places)
                  if (!/^\d{0,3}(\.\d{0,2})?$/.test(inputValue)) {
                    return;
                  }

                  const numericValue = parseFloat(inputValue);

                  // Prevent value > 100
                  if (numericValue > 100) {
                    return;
                  }

                  // Prevent value 0 or 0.00
                  if (numericValue === 0) {
                    return;
                  }

                  formik.handleChange(val);
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.vat && formik.errors.vat}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default Settings;
